/* eslint-disable react-hooks/exhaustive-deps */
import clientSvc from '@efex-services/client/clientSvc';
import { IValidateContactEmailCamelCase } from '@efex-services/client/types';
import BuildNewContactService from '@pages/clients/services/buildNewContact.service';
import { selectProfileData } from '@pages/profile/store/selectors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import { registerFormSteps } from '../constants';
import { useFormData } from '../context';
import { CreateContactPayload, ExistingContact } from '../types';

interface AddContactFormProps {
  onClose: () => void;
}

const useAddContact = ({ onClose }: AddContactFormProps) => {
  const { t } = useTranslation();
  const buildNewContact = new BuildNewContactService();
  const userProfile = useSelector(selectProfileData);
  const {
    data,
    contactDB,
    setFormValues,
    setContactDbValues,
    clearFormValues,
  } = useFormData();

  const [formStep, setFormStep] = useState(0);
  const [steps, setSteps] = useState([t('generalInformation')]);
  const [selectedExistingContact, setSelectedExistingContact] = useState<
    IValidateContactEmailCamelCase | undefined
  >(undefined);

  const {
    data: responseExistingData,
    error: responseExistingError,
    invoke: createClientFromExisting,
    loading: responseExistingLoading,
  } = useApiCall(({ uuid, email, name }) =>
    clientSvc.createClientFromExisting({ uuid, email, name })
  );

  const {
    data: responseData,
    error: responseError,
    invoke: createContact,
    loading: responseLoading,
  } = useApiCall(
    ({
      values,
      enterpriseId,
    }: {
      values: CreateContactPayload;
      enterpriseId: string;
    }) => clientSvc.createContact({ values, enterpriseId })
  );

  const nextFormStep = () => setFormStep((currentStep) => currentStep + 1);

  const prevFormStep = () => setFormStep((currentStep) => currentStep - 1);

  const createNewContact = async () => {
    const formattedUser = buildNewContact.build(data);
    const enterpriseId = userProfile?.company?.enterpriseId;
    try {
      if (enterpriseId) {
        await createContact({ values: formattedUser, enterpriseId });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createContactFromExistingContact = async () => {
    const filteredSelectedContact = (
      contactDB as IValidateContactEmailCamelCase[]
    ).find((opt) => opt.naturalPerson.id === data.contactId);
    try {
      await createClientFromExisting({
        uuid: filteredSelectedContact?.naturalPerson.uuid || '',
        email: filteredSelectedContact?.naturalPerson.email || '',
        name: filteredSelectedContact?.naturalPerson.firstName || '',
      });
    } catch (error) {
      console.error(error);
    }
  };

  const createFromExist = async ({ uuid, email, name }: ExistingContact) => {
    try {
      setFormValues({ email });
      await createClientFromExisting({ uuid, email, name });
    } catch (error) {
      console.error(error);
    }
  };

  const closeAndClearContext = () => {
    clearFormValues();
    setContactDbValues(undefined);
    onClose();
  };

  useEffect(() => {
    if (data?.data?.value > 0 && contactDB?.length) {
      const getContact = (contactDB as IValidateContactEmailCamelCase[]).find(
        (opt) => opt.naturalPerson.id === data.data.value
      );
      setSelectedExistingContact(getContact);
    }
    if (!contactDB || !contactDB.length) {
      setSelectedExistingContact(undefined);
      setSelectedExistingContact(undefined);
    }
  }, [data, contactDB]);

  useEffect(() => {
    if (selectedExistingContact && formStep > 0) {
      setSteps(['step1', 'step2']);
      return;
    }
    if (!selectedExistingContact && formStep > 0) {
      setSteps(['step1', 'step2', 'step3']);
    }
  }, [selectedExistingContact, formStep]);

  useEffect(() => {
    clearFormValues();
    setContactDbValues(undefined);
  }, []);

  useEffect(() => {
    if (responseExistingData?.id || responseData?.enterprise.id) {
      setFormStep(registerFormSteps.CONTACT_CONFIRMATION);
    }
  }, [responseExistingData, responseData]);

  return {
    formStep,
    steps,
    responseExistingLoading,
    responseLoading,
    responseError,
    responseExistingError,
    selectedExistingContact,
    data,
    responseExistingData,
    responseData,
    createNewContact,
    createContactFromExistingContact,
    createFromExist,
    nextFormStep,
    prevFormStep,
    closeAndClearContext,
    setFormStep,
  };
};

export default useAddContact;
